import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, KeyRound, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AuthShell, Field, inputCls } from "@/components/nadia/AuthShell";
import { authForgot, authReset, formatApiError } from "@/lib/api";

/** /password-dimenticata (request) and /reimposta-password?token= (reset) share this page. */
export default function PasswordDimenticata() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);
  const nav = useNavigate();

  const request = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await authForgot(email);
      setSent(r);
      toast.info("Richiesta acquisita. La consegna dell'email non è ancora confermata.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const reset = async (e) => {
    e.preventDefault();
    if (pwd !== pwd2) return toast.error("Le password non coincidono.");
    setBusy(true);
    try {
      await authReset(token, pwd);
      toast.success("Password aggiornata. Ora puoi accedere.");
      nav("/accedi");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (token) {
    return (
      <AuthShell eyebrow="Recupero password" title="Scegli una nuova password" footer={<Link to="/accedi" className="text-cyan-300 hover:text-cyan-200">Torna all'accesso</Link>}>
        <form onSubmit={reset} className="space-y-4" data-testid="reset-form">
          <Field label="Nuova password"><Input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} required minLength={8} className={inputCls} placeholder="Almeno 8 caratteri" data-testid="reset-password" /></Field>
          <Field label="Conferma password"><Input type="password" value={pwd2} onChange={(e) => setPwd2(e.target.value)} required className={inputCls} data-testid="reset-password-confirm" /></Field>
          <Button type="submit" disabled={busy} className="w-full h-12 bg-gradient-to-r from-cyan-400 to-violet-500 text-slate-950 font-semibold rounded-xl" data-testid="reset-submit">
            {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <KeyRound size={16} className="mr-2" />} Salva nuova password
          </Button>
        </form>
      </AuthShell>
    );
  }

  return (
    <AuthShell eyebrow="Recupero password" title="Password dimenticata?" subtitle="Inserisci la tua email per richiedere il link di recupero." footer={<Link to="/accedi" className="text-cyan-300 hover:text-cyan-200" data-testid="forgot-back">Torna all'accesso</Link>}>
      {sent ? (
        <div className="space-y-3" data-testid="forgot-sent">
          <div className="text-sm text-slate-200" data-testid="forgot-request-status">{sent.message || "Richiesta acquisita. Questa conferma non attesta la consegna dell'email."}</div>
          <div className="text-xs text-slate-500">Controlla anche la cartella della posta indesiderata.</div>
        </div>
      ) : (
        <form onSubmit={request} className="space-y-4" data-testid="forgot-form">
          <Field label="Email"><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className={inputCls} placeholder="nome@azienda.it" data-testid="forgot-email" /></Field>
          <Button type="submit" disabled={busy} className="w-full h-12 bg-gradient-to-r from-cyan-400 to-violet-500 text-slate-950 font-semibold rounded-xl" data-testid="forgot-submit">
            {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Mail size={16} className="mr-2" />} Inviami il link
          </Button>
        </form>
      )}
    </AuthShell>
  );
}
